import { CloseRounded } from '@mui/icons-material'
import { Button, DialogActions, DialogContent, DialogTitle, TextField, Typography, useTheme, Autocomplete, Box } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { toggleFormCourse } from '../../../../../redux/actions/courses'
import { resetFormValidator } from '../../../../../redux/actions/validator'
import { IRootState } from '../../../../../redux/types'
import TabPanel from '../../../../shared/components/TabPanel'
import {
  BrandAutocomplete,
  CancelButton,
  Dropzone,
  RemoveContent,
  LanguageMultiAutocomplete,
  LineAutocomplete,
  RoleAutocomplete,
  SaveButton,
  VisibilityAutocomplete
} from '../../../../shared/components/inputs'
import InputCountry from '../../../../shared/components/inputs/InputCountry'
import { formsStyles } from '../../../../shared/styles/FormsStyles'
import useTabState from '@/contexts/shared/hooks/useTabState'
import { StyledDialog } from '@/contexts/shared/styles/StyledDialog'
import StatusAutocomplete from '@/contexts/shared/components/inputs/StatusAutocomplete'
import CustomTextField from '@/contexts/shared/components/inputs/CustomTextField'
import useCourseState from '../hooks/modal/useCourseState'
import useHandleSubmit from '../hooks/modal/useHandleSubmit'
import useInitialState from '../hooks/modal/useInitialState'
import { StyledTabs } from '@/contexts/shared/components/StyledTabs'
import { CustomTab } from '@/contexts/backoffice/users/ui/components/modals/create/CustomTab'

function CoursesModal() {
  const { t } = useTranslation(['global'])
  const theme = useTheme()
  const formClasses = formsStyles(theme)
  const dispatch = useDispatch()

  const { course_selected } = useSelector((state: IRootState) => state.course)
  const { showForm } = useSelector((state: IRootState) => state.course)
  const { coursetypes } = useSelector((state: IRootState) => state.coursetype)
  const { users } = useSelector((state: IRootState) => state.user)
  const { isValidForm, errorForm, errorTab, msgForm } = useSelector((state: IRootState) => state.validator)

  const handleCloseModalCourse = () => {
    dispatch(resetFormValidator())
    dispatch(toggleFormCourse())
  }

  const { STATUS, VISIBILITY } = useInitialState()
  const { currentTab, setCurrentTab, a11yProps } = useTabState()
  const { handleInputChange, handleMultiAutoCompleteChange, courseState } = useCourseState(STATUS, VISIBILITY)
  const { handleSubmit } = useHandleSubmit(courseState, handleCloseModalCourse)

  const handleRemoveImage = () => {
    handleInputChange('file_image', null)
  }

  return (
    <StyledDialog
      open={showForm}
      onClose={handleCloseModalCourse}
      aria-labelledby="course-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="course-dialog-title">
        <Grid container justifyContent="space-between">
          <Typography variant="h6">
            {courseState.id !== null ? t('edit') : t('create')} {t('course')}
          </Typography>
          <Button onClick={handleCloseModalCourse} color="primary" variant="text">
            <CloseRounded />
          </Button>
        </Grid>
      </DialogTitle>

      <DialogContent sx={{ pb: 3, pt: 2 }}>
        <Grid container alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Grid xs={12} sm={6} md={courseState.brand != null ? 6 : 8}>
            <CustomTextField inputName="title" value={courseState.title} handleInputChange={handleInputChange} />
          </Grid>
          <Grid xs={12} sm={6} md={courseState.brand != null ? 3 : 4}>
            <BrandAutocomplete value={courseState.brand} handleInputChange={handleInputChange} props={{ required: true }} />
          </Grid>
          {courseState.brand != null && (
            <Grid xs={12} sm={6} md={courseState.brand != null ? 3 : 4}>
              <LineAutocomplete value={courseState.line} brand={courseState.brand} handleInputChange={handleInputChange} />
            </Grid>
          )}
        </Grid>
        <Grid container>
          <Grid xs={12}>
            <StyledTabs value={currentTab} onChange={(_, value) => setCurrentTab(value)} aria-label="course-modal-tabs">
              <CustomTab label={t('general')} {...a11yProps(0)} error={errorTab.general} />
              <CustomTab label={t('add-ons')} {...a11yProps(1)} error={errorTab.addOn} />
            </StyledTabs>
          </Grid>
        </Grid>
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={4}>
              <RoleAutocomplete value={courseState.role} handleInputChange={handleInputChange} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <InputCountry value={courseState.countries} handleInputChange={handleInputChange} required />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <LanguageMultiAutocomplete value={courseState.languages} handleInputChange={handleInputChange} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              {/* TODO: Ver demas componentes para pasarle el options */}
              <StatusAutocomplete value={courseState.status} handleInputChange={handleInputChange} options={STATUS} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              {/* TODO: Ver demas componentes para pasarle el options */}
              <VisibilityAutocomplete value={courseState.visibility} handleInputChange={handleInputChange} options={VISIBILITY} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <Autocomplete
                id="autocomplete-course-responsible"
                fullWidth
                multiple
                value={users.filter((user: any) => courseState.responsibles.includes(user.id))}
                onChange={handleMultiAutoCompleteChange('responsibles')}
                options={users}
                getOptionLabel={(option: any) => (option.id !== undefined ? option.name : '')}
                renderInput={params => (
                  <TextField
                    required
                    {...params}
                    label={t('trainers')}
                    error={errorForm.responsibles}
                    helperText={msgForm.responsibles}
                    variant="filled"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <Autocomplete
                id="autocomplete-course-categories"
                data-createcourse="categories"
                value={courseState.coursetype}
                onChange={(_, value) => handleInputChange('coursetype', value)}
                options={coursetypes}
                getOptionLabel={(option: any) => (option.id !== undefined ? option.name : '')}
                renderInput={params => <TextField {...params} label={t('category')} variant="filled" InputLabelProps={{ shrink: true }} />}
              />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <CustomTextField inputName="requirements" value={courseState.requirements} handleInputChange={handleInputChange} />
            </Grid>
            <Grid xs={12}>
              {courseState.file_image ? (
                <Box my={2}>
                  <RemoveContent
                    files={[`${import.meta.env.VITE_AWS_URL}/courses/${courseState.file_image}`]}
                    onHandleClick={handleRemoveImage}
                  />
                </Box>
              ) : (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    {`${t('image')}:`}
                  </Typography>
                  <Dropzone name={'file_image'} handleInputChange={handleInputChange} dir={'courses'} />
                </>
              )}
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6}>
              <CustomTextField inputName="duration" value={courseState.duration} handleInputChange={handleInputChange} required />
            </Grid>
            <Grid xs={12} md={6}>
              <CustomTextField label="audience" inputName="leaded" value={courseState.leaded} handleInputChange={handleInputChange} />
            </Grid>
            <Grid xs={12} md={12}>
              <CustomTextField multiline inputName="description" value={courseState.description} handleInputChange={handleInputChange} />
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>
      <DialogActions sx={!isValidForm && Object.keys(msgForm).length > 0 ? formClasses.errorDialogActions : undefined}>
        <CancelButton onClick={handleCloseModalCourse}>{t('cancel')}</CancelButton>
        <SaveButton onClick={handleSubmit}>{t('save')}</SaveButton>
      </DialogActions>
    </StyledDialog>
  )
}

export default CoursesModal
